import React, { useEffect, useMemo, useState } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaFile, FaFileImage, FaFilePdf, FaFileWord } from "react-icons/fa";
import GetData, { fetchFileBlob } from "../../../services/getData";
import BlockTitle from "../../../components/layout/blockTitle/BlockTitle";
import EditableModal from "../../../components/layout/editableModal/EditableModal";
import Toast from "../../../components/layout/toast/Toast";
import "./PatientAnalyses.css";
import "./patientInbody.css";
import "../patients.css";

const getAnalysisModalFields = (t) => [
    { name: "date", label: t("analyses.fields.date"), type: "date" },
    { name: "name", label: t("analyses.fields.name"), type: "text", required: true },
    { name: "files", label: t("analyses.fields.file"), type: "file", multiple: false, required: true },
];

const toIsoDate = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatDate = (dateString) => {
    if (!dateString) return "—";
    const [datePart] = String(dateString).split(" ");
    const [year, month, day] = String(datePart).split("-");
    if (!year || !month || !day) return String(dateString);
    return `${day}.${month}.${year}`;
};

const getFileExtension = (fileName) => {
    const parts = String(fileName ?? "").split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const getPrimaryFile = (row) => {
    if (Array.isArray(row?.files) && row.files.length > 0) {
        return row.files[0];
    }
    return null;
};

const getAnalysisDisplayName = (row) => {
    const userName = String(row?.name ?? "").trim();
    const fileName = String(getPrimaryFile(row)?.name ?? "").trim();

    if (userName && fileName) {
        return `${userName} (${fileName})`;
    }
    if (userName) return userName;
    if (fileName) return fileName;
    return "—";
};

const getFileTypeIcon = (fileName) => {
    const ext = getFileExtension(fileName);
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return FaFileImage;
    if (ext === "pdf") return FaFilePdf;
    if (["doc", "docx"].includes(ext)) return FaFileWord;
    return FaFile;
};

const normalizeRecordsRows = (recordsParsed, recordsRaw, t) => {
    if (Array.isArray(recordsParsed) && recordsParsed.length > 0) {
        return recordsParsed.map((item, index) => {
            if (item && typeof item === "object" && !Array.isArray(item)) {
                const keys = Object.keys(item);
                if (keys.length === 1 && Array.isArray(item[keys[0]])) {
                    return normalizeRecordsRows(item[keys[0]], recordsRaw, t);
                }

                return {
                    key: item.id ?? item.code ?? index,
                    label:
                        item.name ??
                        item.title ??
                        item.parameter ??
                        item.label ??
                        item.key ??
                        t("analyses.records.indicatorName", { number: index + 1 }),
                    value:
                        item.value ??
                        item.result ??
                        item.val ??
                        (typeof item === "object" ? JSON.stringify(item) : String(item)),
                    unit: item.unit ?? item.units ?? "",
                    reference: item.reference ?? item.norm ?? item.range ?? "",
                };
            }

            return {
                key: index,
                label: t("analyses.records.indicatorName", { number: index + 1 }),
                value: String(item ?? "—"),
                unit: "",
                reference: "",
            };
        }).flat();
    }

    if (recordsParsed && typeof recordsParsed === "object") {
        return Object.entries(recordsParsed).map(([key, value]) => ({
            key,
            label: key,
            value: typeof value === "object" ? JSON.stringify(value) : String(value ?? "—"),
            unit: "",
            reference: "",
        }));
    }

    const raw = String(recordsRaw ?? "").trim();
    if (!raw) return [];

    try {
        const decoded = JSON.parse(raw);
        return normalizeRecordsRows(decoded, raw, t);
    } catch {
        return [];
    }
};

function AnalysisFilePreview({ file }) {
    const { t } = useTranslation("patient");
    const [blobUrl, setBlobUrl] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const fileId = Number(file?.id);
        if (!fileId) {
            setLoading(false);
            setError(t("analyses.preview.noFile"));
            return undefined;
        }

        let cancelled = false;
        let objectUrl = "";

        (async () => {
            setLoading(true);
            setError("");
            try {
                const blob = await fetchFileBlob(fileId, true);
                objectUrl = URL.createObjectURL(blob);
                if (!cancelled) {
                    setBlobUrl(objectUrl);
                }
            } catch (fetchError) {
                if (!cancelled) {
                    setError(fetchError?.message || t("analyses.preview.loadFailed"));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [file?.id, t]);

    if (loading) {
        return <div className="analysis-view-file__state">{t("analyses.preview.loading")}</div>;
    }

    if (error) {
        return <div className="analysis-view-file__state analysis-view-file__state--error">{error}</div>;
    }

    const ext = getFileExtension(file?.name);

    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
        return (
            <div className="analysis-view-file">
                <img src={blobUrl} alt={file?.name || t("analyses.preview.fileAlt")} className="analysis-view-file__image" />
            </div>
        );
    }

    if (ext === "pdf") {
        return (
            <div className="analysis-view-file">
                <iframe
                    src={blobUrl}
                    title={file?.name || t("analyses.preview.fileAlt")}
                    className="analysis-view-file__frame"
                />
            </div>
        );
    }

    return (
        <div className="analysis-view-file">
            <div className="analysis-view-file__state">{t("analyses.preview.unsupported")}</div>
        </div>
    );
}

function AnalysisRecordsTable({ recordsParsed, recordsRaw }) {
    const { t } = useTranslation("patient");
    const rows = normalizeRecordsRows(recordsParsed, recordsRaw, t);

    if (!rows.length) {
        const raw = String(recordsRaw ?? "").trim();
        if (!raw) {
            return <div className="analysis-view-empty">{t("analyses.records.empty")}</div>;
        }

        return <pre className="analysis-view-records-raw">{raw}</pre>;
    }

    return (
        <div className="analysis-view-records">
            <table className="analysis-view-records-table">
                <thead>
                    <tr>
                        <th>{t("analyses.records.indicator")}</th>
                        <th>{t("analyses.records.value")}</th>
                        <th>{t("analyses.records.unit")}</th>
                        <th>{t("analyses.records.normal")}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.key}>
                            <td>{row.label}</td>
                            <td>{row.value ?? "—"}</td>
                            <td>{row.unit || "—"}</td>
                            <td>{row.reference || "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const initialAnalysesData = {
    date: toIsoDate(new Date()),
    name: "",
    files: [],
};

export default function PatientAnalyses() {
    const { t } = useTranslation("patient");
    const { patientId } = useParams();
    const id_patient = Number(patientId);

    const analysisModalFields = useMemo(() => getAnalysisModalFields(t), [t]);

    const [showAddModal, setShowAddModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);
    const [downloadingFile, setDownloadingFile] = useState(false);
    const [decodingId, setDecodingId] = useState(null);
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);
    const [analyses, setAnalyses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({});

    const showToast = (title, status = "success") => {
        setToast({
            id: Date.now(),
            title,
            status,
        });
    };

    const fetchAnalyses = async () => {
        if (!id_patient) {
            setAnalyses([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const data = await GetData({
                module: "patient",
                block: "analysis",
                id_patient,
            });

            if (Array.isArray(data?.result)) {
                setAnalyses(data.result);
            } else if (data?.result) {
                setAnalyses([data.result]);
            } else {
                setAnalyses([]);
            }
        } catch (error) {
            console.error("Ошибка загрузки анализов:", error);
            setAnalyses([]);
            showToast(t("analyses.toast.loadFailed"), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalyses();
    }, [id_patient]);

    const handleSaveAnalysis = async (formData) => {
        const name = String(formData?.name ?? "").trim();
        if (!name) {
            showToast(t("analyses.toast.enterName"), "error");
            return;
        }

        try {
            const files = Array.isArray(formData?.files) ? formData.files : [];
            const requestData = {
                date: formData?.date || toIsoDate(new Date()),
                name,
            };

            const response = await GetData(
                {
                    module: "patient",
                    block: "analysis",
                    action: "add",
                    id_patient,
                    ...requestData,
                },
                files,
            );

            if (response?.status?.success === false) {
                showToast(response?.status?.message || t("analyses.toast.addFailed"), "error");
                return;
            }

            const uploadErrors = Array.isArray(response?.upload_errors)
                ? response.upload_errors
                : [];
            if (uploadErrors.length > 0) {
                showToast(t("analyses.toast.addedWithErrors", { message: uploadErrors[0] }), "error");
            } else {
                showToast(response?.status?.message || t("analyses.toast.added"), "success");
            }

            setShowAddModal(false);
            fetchAnalyses();
        } catch (error) {
            console.error("Ошибка добавления анализа:", error);
            showToast(t("analyses.toast.addFailed"), "error");
        }
    };

    const handleViewAnalysis = async (row) => {
        if (!row?.id) return;

        setShowViewModal(true);
        setViewLoading(true);
        setSelectedAnalysis(row);

        try {
            const data = await GetData({
                module: "patient",
                block: "analysis",
                action: "view",
                id_patient,
                id: row.id,
            });

            if (data?.status?.success === false) {
                showToast(data?.status?.message || t("analyses.toast.viewFailed"), "error");
                return;
            }

            if (data?.result) {
                setSelectedAnalysis(data.result);
            }
        } catch (error) {
            console.error("Ошибка загрузки анализа:", error);
            showToast(t("analyses.toast.viewFailed"), "error");
        } finally {
            setViewLoading(false);
        }
    };

    const blockTitleButtons = [
        { label: t("analyses.buttons.add"), onClick: () => setShowAddModal(true), type: "light" },
    ];

    const sortedAnalyses = useMemo(
        () =>
            [...analyses].sort((a, b) => {
                const aTime = Date.parse(a?.date || "");
                const bTime = Date.parse(b?.date || "");
                if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) return bTime - aTime;
                return 0;
            }),
        [analyses],
    );

    const rowCanDecodeWithAi = (row) =>
        Boolean(
            row?.has_file
            || (Array.isArray(row?.files) && row.files.length > 0)
            || (Array.isArray(row?.file_ids) && row.file_ids.length > 0)
            || Number(row?.id_files) > 0,
        );

    const primaryFile = getPrimaryFile(selectedAnalysis);

    const handleDownloadFile = async (file) => {
        const fileId = Number(file?.id);
        if (!fileId || downloadingFile) return;

        setDownloadingFile(true);
        try {
            const blob = await fetchFileBlob(fileId, false);
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = file?.name || "file";
            link.click();
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            console.error("Ошибка скачивания файла:", error);
            showToast(error?.message || t("analyses.toast.downloadFailed"), "error");
        } finally {
            setDownloadingFile(false);
        }
    };

    const handleDecodeWithAi = async (row) => {
        const analysisId = Number(row?.id);
        if (!analysisId || !id_patient || decodingId) return;

        if (!rowCanDecodeWithAi(row)) {
            showToast(t("analyses.toast.noFileAttached"), "error");
            return;
        }

        setDecodingId(analysisId);
        try {
            const data = await GetData({
                module: "patient",
                block: "analysis",
                action: "decode_ai",
                id_patient,
                id: analysisId,
            });

            if (data?.status?.success === false) {
                showToast(data?.status?.message || t("analyses.toast.decodeFailed"), "error");
                return;
            }

            if (data?.result) {
                setSelectedAnalysis((prev) =>
                    Number(prev?.id) === analysisId ? data.result : prev,
                );
                setAnalyses((prev) =>
                    prev.map((item) => (Number(item?.id) === analysisId ? { ...item, ...data.result } : item)),
                );
            }

            showToast(data?.status?.message || t("analyses.toast.decoded"), "success");
        } catch (error) {
            console.error("Ошибка расшифровки анализа:", error);
            showToast(error?.message || t("analyses.toast.decodeFailed"), "error");
        } finally {
            setDecodingId(null);
        }
    };

    return (
        <div className="patient-dynamics patient-analyses">
            {toast.title ? <Toast key={toast.id} title={toast.title} status={toast.status} /> : false}

            <div className="patient-name-title-sticky">
                <BlockTitle title={t("analyses.title")} buttons={blockTitleButtons} />
            </div>

            <Row className="mt-4">
                <Col>
                    <div className="history-section">
                        <div className="history-table-container">
                            <table className="history-table analysis-list-table">
                                <thead>
                                    <tr>
                                        <th>{t("analyses.table.name")}</th>
                                        <th>{t("analyses.table.date")}</th>
                                        <th className="analysis-list-table__actions-col">{t("analyses.table.actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={3} className="empty-state">
                                                {t("analyses.table.loading")}
                                            </td>
                                        </tr>
                                    ) : sortedAnalyses.length ? (
                                        sortedAnalyses.map((row) => {
                                            const isDecodingRow = Number(decodingId) === Number(row?.id);
                                            const canDecodeRow = rowCanDecodeWithAi(row);
                                            const rowFile = getPrimaryFile(row);
                                            const FileIcon = getFileTypeIcon(rowFile?.name);
                                            const displayName = getAnalysisDisplayName(row);
                                            const canDownload = Boolean(Number(rowFile?.id));
                                            return (
                                                <tr key={row?.id ?? `${row?.date}-${row?.name}`}>
                                                    <td>
                                                        <div className="analysis-list-table__name">
                                                            <FileIcon
                                                                className="analysis-list-table__name-icon"
                                                                aria-hidden="true"
                                                            />
                                                            {canDownload ? (
                                                                <button
                                                                    type="button"
                                                                    className="analysis-list-table__name-link"
                                                                    onClick={() => handleDownloadFile(rowFile)}
                                                                    disabled={downloadingFile}
                                                                    title={t("analyses.table.downloadFile")}
                                                                >
                                                                    {displayName}
                                                                </button>
                                                            ) : (
                                                                <span>{displayName}</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>{formatDate(row?.date)}</td>
                                                    <td className="analysis-list-table__actions-col">
                                                        <div className="analysis-list-table__actions">
                                                            <button
                                                                type="button"
                                                                className="dynamics-action-btn dynamics-action-btn--decode"
                                                                onClick={() => handleDecodeWithAi(row)}
                                                                disabled={Boolean(decodingId) || !canDecodeRow}
                                                                title={
                                                                    canDecodeRow
                                                                        ? t("analyses.table.decodeAi")
                                                                        : t("analyses.table.decodeAiHint")
                                                                }
                                                                aria-label={t("analyses.table.decodeAiAria")}
                                                            >
                                                                {isDecodingRow ? (
                                                                    <span className="dynamics-action-spinner" aria-hidden="true" />
                                                                ) : (
                                                                    t("analyses.buttons.ai")
                                                                )}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="analysis-view-btn"
                                                                onClick={() => handleViewAnalysis(row)}
                                                            >
                                                                {t("analyses.buttons.view")}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={3} className="empty-state">
                                                {t("analyses.table.empty")}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Col>
            </Row>

            <EditableModal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                title={t("analyses.addTitle")}
                initialData={initialAnalysesData}
                fields={analysisModalFields}
                onSave={handleSaveAnalysis}
                closeOnSave={false}
            />

            <Modal
                show={showViewModal}
                onHide={() => {
                    setShowViewModal(false);
                    setSelectedAnalysis(null);
                }}
                size="lg"
                centered
                className="analysis-view-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{selectedAnalysis?.name || t("analyses.view.fallbackTitle")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {viewLoading && !selectedAnalysis ? (
                        <div className="analysis-view-empty">{t("analyses.view.loading")}</div>
                    ) : selectedAnalysis ? (
                        <div className="analysis-view-content">
                            <section className="analysis-view-section">
                                <h3 className="analysis-view-section__title">{t("analyses.view.date")}</h3>
                                <div className="analysis-view-section__body">
                                    {formatDate(selectedAnalysis?.date)}
                                </div>
                            </section>

                            <section className="analysis-view-section">
                                <h3 className="analysis-view-section__title">{t("analyses.view.results")}</h3>
                                <AnalysisRecordsTable
                                    recordsParsed={selectedAnalysis?.records_parsed}
                                    recordsRaw={selectedAnalysis?.records}
                                />
                            </section>

                            <section className="analysis-view-section">
                                <h3 className="analysis-view-section__title">{t("analyses.view.file")}</h3>
                                {primaryFile ? (
                                    <AnalysisFilePreview file={primaryFile} />
                                ) : (
                                    <div className="analysis-view-empty">{t("analyses.preview.noFile")}</div>
                                )}
                            </section>
                        </div>
                    ) : (
                        <div className="analysis-view-empty">{t("analyses.view.notFound")}</div>
                    )}
                </Modal.Body>
                {selectedAnalysis ? (
                    <Modal.Footer className="analysis-view-modal__footer">
                        <button
                            type="button"
                            className="button-default light"
                            onClick={() => handleDownloadFile(primaryFile)}
                            disabled={!primaryFile || downloadingFile}
                        >
                            {downloadingFile ? t("analyses.view.downloading") : t("analyses.view.download")}
                        </button>
                    </Modal.Footer>
                ) : null}
            </Modal>
        </div>
    );
}
